import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { query } from '@warp-drive/utilities/json-api';
import type { EmployeeExtension } from '../data/resources/employee.ext.ts';

const companiesUrl = (path?: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'companies' },
    op: 'query',
    resourcePath: 'companies',
  };
  const base = buildBaseURL(opts);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
};

/**
 * GET /companies/:id/tas-approvers — materializes eligible Employee records.
 * Each execution reloads the role list from the server while retaining record identity.
 * @param companyId Persisted company identifier used in the nested collection path.
 * @returns A typed query whose execution propagates network and API failures.
 */
export function getTasApproversRequest(companyId: string) {
  return query<EmployeeExtension>(
    'employee',
    {},
    {
      resourcePath: `companies/${companyId}/tas-approvers`,
      reload: true,
    }
  );
}

/**
 * GET /companies/:id/account-managers — materializes the TIO Employee records holding
 * ACCOUNT_MANAGER relationships. The Store owns deduplication and cache expiry; display
 * projections should read these live records when rendered, not snapshot their names.
 * @param companyId Persisted company identifier used in the nested collection path.
 * @returns A typed, cache-enabled query whose execution propagates network and API failures.
 */
export function getAccountManagersRequest(companyId: string) {
  return query<EmployeeExtension>(
    'employee',
    {},
    {
      resourcePath: `companies/${companyId}/account-managers`,
    }
  );
}

export interface AccountManager {
  id: string;
  fullName: string;
  email: string;
}

/**
 * Projects live employee fields for account-manager labels, ignoring malformed name/email values.
 * @param employee A persisted employee's current name and email fields.
 * @returns A navigation identifier with string-only display values.
 * @throws When the employee has no persisted identifier.
 */
export function accountManagerFromEmployee(employee: {
  id: string | null;
  firstName?: unknown;
  lastName?: unknown;
  email?: unknown;
}): AccountManager {
  const { id, firstName, lastName, email } = employee;
  if (!id) throw new Error('Account-manager navigation requires a persisted employee identifier');
  return {
    id,
    fullName: [firstName, lastName]
      .filter((part): part is string => typeof part === 'string' && part.length > 0)
      .join(' '),
    email: typeof email === 'string' ? email : '',
  };
}

/** GET /companies/:id/eligibility-employee-counts — admin dashboard widget. */
export function getEligibilityCountsRequest(companyId: string): RequestInfo {
  return {
    url: companiesUrl(`${companyId}/eligibility-employee-counts`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/** GET /companies/:id/participation-employee-counts — admin dashboard widget. */
export function getParticipationCountsRequest(companyId: string): RequestInfo {
  return {
    url: companiesUrl(`${companyId}/participation-employee-counts`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}
